import { Table } from "antd"
import { ColumnProps } from "antd/lib/table"
import React, { FC, memo } from "react"
import { useTranslation } from "react-i18next"
import {
    Course,
    GetCourseLessonsByCourseIdQuery,
    PaymentInterval,
    useGetCourseLessonsByCourseIdQuery,
} from "../../generated/graphql"
import i18n from "../../services/i18n"
import {
    renderDateAsWeekdayTag,
    renderDateFormatted,
    renderDuration,
    renderStartEndTime,
} from "../TableColumnRenderers/TableColumnRenderers"
import css from "./CourseLessonTable.module.css"
import { sortByStartDateTime } from "../../helpers/sortingFunctions"
import { QueryItem } from "../../types"

// type CourseLessonType = Pick<CourseLesson, "id" | "startDateTime" | "endDateTime" | "locationId">

type CourseLessonTableProps = {
    courseId: Course["id"]
    totalPrice?: number
    paymentInterval?: PaymentInterval
    freePlaces?: number
}

type GetCourseLessonsByCourseIdQueryItem = QueryItem<GetCourseLessonsByCourseIdQuery["courseLessonsByCourseId"]>

type TableType = GetCourseLessonsByCourseIdQuery["courseLessonsByCourseId"]["0"]

// Reine Anzeige-Logik: liegt der Termin vor JETZT (Client-Uhr)? Keinerlei Preislogik.
const isLessonInPast = (startDateTime: string): boolean => new Date(startDateTime).getTime() < Date.now()

const CourseLessonTable: FC<CourseLessonTableProps> = ({
    courseId,
    // courseLessons,
    totalPrice,
    paymentInterval,
    freePlaces,
}) => {
    const { t } = useTranslation()
    const pageSize = 10
    const [currentTablePage, setCurrentTablePage] = React.useState(0)
    const { data, loading } = useGetCourseLessonsByCourseIdQuery({
        variables: { courseId },
    })

    const courseLessons: Array<GetCourseLessonsByCourseIdQueryItem> = data?.courseLessonsByCourseId ?? []

    const columns: Array<ColumnProps<TableType>> = [
        {
            title: "#",
            dataIndex: "number",
            render: (text, record, index) => index + currentTablePage * pageSize + 1,
        },
        {
            title: i18n.containers.courses.CourseCreate.courseStartDateTime.label(),
            dataIndex: "dayOfTheWeek",
            align: "center",
            render: (text, record) =>
                renderDateAsWeekdayTag({
                    value: record.startDateTime,
                    style: {
                        width: "100%",
                        textAlign: "center",
                        fontWeight: 600,
                    },
                }),
        },
        {
            title: i18n.containers.courses.CourseCreate.courseStartDateTime.label(),
            dataIndex: "startDateTime",
            className: css.dateColumn,
            align: "center",
            render: (text, record) => (
                <>
                    {renderDateFormatted(record.startDateTime, "DD. MMM YYYY")}
                    {isLessonInPast(record.startDateTime) && (
                        <div className={css.lessonInPastTag} data-testid='lesson-in-past-tag'>
                            {t("lateEntry.lessonInPast", "bereits stattgefunden")}
                        </div>
                    )}
                </>
            ),
            // defaultSortOrder: "descend",
            sorter: sortByStartDateTime,
            sortDirections: [],
            sortOrder: "ascend",
        },
        {
            title: i18n.containers.courses.CourseCreate.courseStartDateTime.label(),
            dataIndex: "times",
            align: "center",
            render: (text, record) =>
                renderStartEndTime({
                    startTime: record.startDateTime,
                    endTime: record.endDateTime,
                }),
        },
        {
            title: i18n.containers.courses.CourseCreate.courseStartDateTime.label(),
            dataIndex: "duration",
            align: "right",
            render: (text, record) =>
                renderDuration({
                    startTime: record.startDateTime,
                    endTime: record.endDateTime,
                }),
        },
    ]

    return (
        <div className={css.root}>
            <Table
                className={css.courseLessonTable}
                id='courseLessonTable'
                loading={loading}
                showHeader={false}
                size='small'
                rowKey='id'
                columns={columns}
                dataSource={courseLessons}
                pagination={{
                    pageSize,
                    hideOnSinglePage: true,
                    position: ["bottomCenter"],
                    size: "small",
                    showSizeChanger: false,
                    showQuickJumper: false,
                    showTotal: () => null,
                    // Bewusst ohne Scroll: window.scrollTo(0) war im iFrame wirkungslos (iframe-resizer) und würde
                    // im Embed die Kundenseite vom Tabellenplatz wegziehen (Tabelle sitzt mitten in der Buchungsübersicht)
                    onChange: (page) => {
                        setCurrentTablePage(page - 1)
                    },
                }}
                rowClassName={(record) => (isLessonInPast(record.startDateTime) ? `${css.extraSmallRow} ${css.lessonInPastRow}` : css.extraSmallRow)}
            />
        </div>
    )
}

export default memo(CourseLessonTable)
